import { InjectedPropsProvider } from 'Components/Core/InjectedPropsContext';
import { WorksheetBatchUpdateAllTestsTypeId } from 'Components/WorksheetBatchUpdate/WorksheetBatchUpdateDefinitions';
import { getConfigForTest } from 'Components/Worksheets/Config';
import { getWorksheetProviderProps } from 'Components/Worksheets/Core/WorksheetEditorInjectedPropsProvider';
import {
  WorksheetInjectedConsumerProps,
  WorksheetInjectedPropsModifier,
} from 'Components/Worksheets/Core/WorksheetEditorInjectedPropsProvider/types';
import { actions } from 'Data/ReduxViewModels/WorksheetBatchUpdate';
import * as oPath from 'Lib/Object/Path/Immutable';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'rxjs';

/**
 * Provider that injects worksheet properties for the fields to enter for all worksheets
 *
 * We configure these fields based on a phantom worksheet comprised of just the entered values.
 */
let WorksheetBatchUpdateUpdateDataInjectedPropsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const locked = useSelector(
    (state) =>
      !state.worksheetBatchUpdate.status.initialised ||
      state.worksheetBatchUpdate.status.applyingUpdate ||
      state.worksheetBatchUpdate.status.checking
  );
  const firstWorksheetId = useSelector(
    (state) => Object.keys(state.worksheetBatchUpdate.worksheets).find((_) => true) ?? ''
  );

  const typeId = useSelector((state) => state.worksheetBatchUpdate.typeId);

  const config = useMemo(
    () => (typeId && typeId !== WorksheetBatchUpdateAllTestsTypeId ? getConfigForTest(typeId) : null),
    [typeId]
  );

  const getProviderProps = useCallback(
    (consumerProps: WorksheetInjectedConsumerProps, modifiers: Array<WorksheetInjectedPropsModifier>) =>
      getWorksheetProviderProps(
        consumerProps,
        {
          locked,
          setValue: (opts) => dispatch(actions.setUpdateDataValue(opts)),
          synchronise: noop,
          worksheetSelector: (state) => oPath.setMultiple({}, '', state.worksheetBatchUpdate.updateData, true),
          worksheetConfigSelector: (_) => config,
          // All worksheets should have the same type, so take the type model from the first worksheet returned.
          worksheetTypeSelector: (state) => state.worksheetBatchUpdate.worksheets[firstWorksheetId]?.worksheetType,
          // This is not a real worksheet, but the worksheet provider props require a worksheet id to be provided.
          worksheetId: '00000000-0000-0000-0000-000000000000',
        },
        modifiers
      ),
    [locked, firstWorksheetId, config, dispatch]
  );

  return <InjectedPropsProvider getProviderProps={getProviderProps}>{children}</InjectedPropsProvider>;
};

export { WorksheetBatchUpdateUpdateDataInjectedPropsProvider };
export default WorksheetBatchUpdateUpdateDataInjectedPropsProvider;
